/**
 * OtrosSection Component
 * Section for manually adding miscellaneous items with automatic calculations
 * Supports currency selection (Soles/USD), exchange rate, IGV (18%), and templates
 */
import React, { useState, useEffect } from "react";
import { Plus, Trash2, MoreHorizontal, RefreshCw, Save, BookmarkPlus, Bookmark } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { toast } from "sonner";
import api from "@/lib/axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const formatNumber = (value, decimals = 2) => {
  return (value || 0).toLocaleString('es-PE', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
};

const IGV_RATE = 0.18;
const DEFAULT_EXCHANGE_RATE = 3.75;

const OtrosSection = ({
  otrosItems = [],
  setOtrosItems,
  otrosCurrency = "PEN",
  setOtrosCurrency,
  exchangeRate = DEFAULT_EXCHANGE_RATE,
  setExchangeRate,
  otrosSubtotal = 0,
  otrosIgv = 0,
  otrosTotal = 0
}) => {
  const [newItem, setNewItem] = useState({
    item: "",
    description: "",
    unit: "und",
    quantity: 1,
    unit_cost: 0
  });
  
  // Templates state
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);

  // Load templates on mount
  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const response = await api.get(`${API}/otros-templates`);
      setTemplates(response.data.templates || []);
    } catch (error) {
      // Silent fail - templates are optional
      // Templates not loaded yet - this is expected during initial render
    } finally {
      setLoadingTemplates(false);
    }
  };

  const saveAsTemplate = async (item) => {
    setSavingTemplate(true);
    try {
      await api.post(`${API}/otros-templates`, {
        item: item.item,
        description: item.description,
        unit: item.unit,
        unit_cost: item.unit_cost
      });
      toast.success(`Template "${item.item}" guardado`);
      loadTemplates();
    } catch (error) {
      toast.error('Error al guardar template');
    } finally {
      setSavingTemplate(false);
    }
  };

  const deleteTemplate = async (templateId) => {
    try {
      await api.delete(`${API}/otros-templates/${templateId}`);
      toast.success('Template eliminado');
      loadTemplates();
    } catch (error) {
      toast.error('Error al eliminar template');
    }
  };

  const applyTemplate = (template) => {
    setNewItem({
      item: template.item,
      description: template.description,
      unit: template.unit,
      quantity: 1,
      unit_cost: template.unit_cost
    });
    setShowTemplates(false);
    toast.info(`Template "${template.item}" cargado`);
  };

  // Calculate item total
  const calculateItemTotal = (item) => {
    return (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_cost) || 0);
  };

  // Add new item
  const handleAddItem = () => {
    if (!newItem.item.trim()) {
      return;
    }

    const itemWithId = {
      ...newItem,
      id: `otros-${Date.now()}`,
      total: calculateItemTotal(newItem)
    };

    setOtrosItems([...otrosItems, itemWithId]);
    setNewItem({
      item: "",
      description: "",
      unit: "und",
      quantity: 1,
      unit_cost: 0
    });
  };

  // Update existing item
  const handleUpdateItem = (index, field, value) => {
    const updatedItems = [...otrosItems];
    updatedItems[index] = {
      ...updatedItems[index],
      [field]: value
    };
    // Recalculate total
    updatedItems[index].total = calculateItemTotal(updatedItems[index]);
    setOtrosItems(updatedItems);
  };

  // Delete item
  const handleDeleteItem = (index) => {
    const updatedItems = otrosItems.filter((_, i) => i !== index);
    setOtrosItems(updatedItems);
  };

  // Handle Enter key to add item
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && newItem.item.trim()) {
      e.preventDefault();
      handleAddItem();
    }
  };

  const currencySymbol = otrosCurrency === "USD" ? "$" : "S/.";
  const altCurrencySymbol = otrosCurrency === "USD" ? "S/." : "$";

  // Convert total to alternate currency
  const convertedTotal = otrosCurrency === "USD" 
    ? otrosTotal * exchangeRate 
    : otrosTotal / exchangeRate;

  return (
    <Card className="border-purple-200 bg-purple-50/30">
      <CardHeader className="py-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="text-sm flex items-center gap-2 text-purple-700">
              <MoreHorizontal className="w-4 h-4" />
              Otros
            </CardTitle>
            <CardDescription className="text-xs">
              Items adicionales con IGV (18%)
            </CardDescription>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {/* Templates Button */}
            <Popover open={showTemplates} onOpenChange={setShowTemplates}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-8 text-xs border-purple-300">
                  <Bookmark className="w-3 h-3 mr-1" />
                  Templates ({templates.length})
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-2" align="end">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Templates Guardados</span>
                    <Button variant="ghost" size="sm" onClick={loadTemplates} disabled={loadingTemplates}>
                      <RefreshCw className={`w-3 h-3 ${loadingTemplates ? 'animate-spin' : ''}`} />
                    </Button>
                  </div>
                  {templates.length === 0 ? (
                    <p className="text-xs text-slate-500 py-2 text-center">
                      No hay templates guardados
                    </p>
                  ) : (
                    <div className="max-h-48 overflow-y-auto space-y-1">
                      {templates.map((template) => (
                        <div key={template.id} className="flex items-center justify-between p-2 bg-slate-50 rounded hover:bg-slate-100">
                          <button
                            onClick={() => applyTemplate(template)}
                            className="flex-1 text-left"
                          >
                            <p className="text-xs font-medium">{template.item}</p>
                            <p className="text-[10px] text-slate-500">{template.description}</p>
                            <p className="text-[10px] text-purple-600">{template.unit} - {currencySymbol}{template.unit_cost}</p>
                          </button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                            onClick={() => deleteTemplate(template.id)}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </PopoverContent>
            </Popover>

            {/* Exchange Rate */}
            <div className="flex items-center gap-1">
              <Label className="text-xs text-purple-600">T.C.:</Label>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                value={exchangeRate}
                onChange={(e) => setExchangeRate(parseFloat(e.target.value) || DEFAULT_EXCHANGE_RATE)}
                className="w-20 h-8 text-xs text-center"
                data-testid="exchange-rate-input"
              />
            </div>

            {/* Currency Selector */}
            <div className="flex items-center gap-1">
              <Label className="text-xs text-purple-600">Moneda:</Label>
              <Select value={otrosCurrency} onValueChange={setOtrosCurrency}>
                <SelectTrigger className="w-24 h-8 text-xs" data-testid="otros-currency-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PEN">S/. Soles</SelectItem>
                  <SelectItem value="USD">$ USD</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Add new item form */}
        <div className="grid grid-cols-12 gap-2 p-2 bg-purple-100/50 rounded-lg border border-purple-200">
          <div className="col-span-2">
            <Label className="text-[10px] text-purple-600">Item</Label>
            <Input
              value={newItem.item}
              onChange={(e) => setNewItem({ ...newItem, item: e.target.value })}
              onKeyDown={handleKeyDown}
              placeholder="Código"
              className="h-8 text-xs"
              data-testid="otros-new-item"
            />
          </div>
          <div className="col-span-3">
            <Label className="text-[10px] text-purple-600">Descripción</Label>
            <Input
              value={newItem.description}
              onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
              onKeyDown={handleKeyDown}
              placeholder="Descripción del item"
              className="h-8 text-xs"
              data-testid="otros-new-description"
            />
          </div>
          <div className="col-span-2">
            <Label className="text-[10px] text-purple-600">Unidad</Label>
            <Select 
              value={newItem.unit} 
              onValueChange={(val) => setNewItem({ ...newItem, unit: val })}
            >
              <SelectTrigger className="h-8 text-xs" data-testid="otros-new-unit">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="und">und</SelectItem>
                <SelectItem value="m²">m²</SelectItem>
                <SelectItem value="ml">ml</SelectItem>
                <SelectItem value="kg">kg</SelectItem>
                <SelectItem value="glb">glb</SelectItem>
                <SelectItem value="día">día</SelectItem>
                <SelectItem value="mes">mes</SelectItem>
                <SelectItem value="hr">hr</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-1">
            <Label className="text-[10px] text-purple-600">Cant.</Label>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={newItem.quantity}
              onChange={(e) => setNewItem({ ...newItem, quantity: parseFloat(e.target.value) || 0 })}
              onKeyDown={handleKeyDown}
              className="h-8 text-xs"
              data-testid="otros-new-quantity"
            />
          </div>
          <div className="col-span-2">
            <Label className="text-[10px] text-purple-600">C. Unit. ({currencySymbol})</Label>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={newItem.unit_cost || ""}
              onChange={(e) => setNewItem({ ...newItem, unit_cost: parseFloat(e.target.value) || 0 })}
              onKeyDown={handleKeyDown}
              placeholder="0.00"
              className="h-8 text-xs"
              data-testid="otros-new-cost"
            />
          </div>
          <div className="col-span-2 flex items-end">
            <Button
              size="sm"
              onClick={handleAddItem}
              disabled={!newItem.item.trim()}
              className="h-8 w-full bg-purple-600 hover:bg-purple-700"
              data-testid="otros-add-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Agregar
            </Button>
          </div>
        </div>

        {/* Items table */}
        {otrosItems.length > 0 ? (
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-purple-50">
                  <TableHead className="text-xs w-20">Item</TableHead>
                  <TableHead className="text-xs">Descripción</TableHead>
                  <TableHead className="text-xs w-16 text-center">Unidad</TableHead>
                  <TableHead className="text-xs w-16 text-right">Cant.</TableHead>
                  <TableHead className="text-xs w-24 text-right">C. Unit.</TableHead>
                  <TableHead className="text-xs w-24 text-right">Total</TableHead>
                  <TableHead className="text-xs w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {otrosItems.map((item, index) => (
                  <TableRow key={item.id || index} data-testid={`otros-item-row-${index}`}>
                    <TableCell className="p-1">
                      <Input
                        value={item.item}
                        onChange={(e) => handleUpdateItem(index, 'item', e.target.value)}
                        className="h-7 text-xs"
                      />
                    </TableCell>
                    <TableCell className="p-1">
                      <Input
                        value={item.description}
                        onChange={(e) => handleUpdateItem(index, 'description', e.target.value)}
                        className="h-7 text-xs"
                      />
                    </TableCell>
                    <TableCell className="p-1">
                      <Select 
                        value={item.unit} 
                        onValueChange={(val) => handleUpdateItem(index, 'unit', val)}
                      >
                        <SelectTrigger className="h-7 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="und">und</SelectItem>
                          <SelectItem value="m²">m²</SelectItem>
                          <SelectItem value="ml">ml</SelectItem>
                          <SelectItem value="kg">kg</SelectItem>
                          <SelectItem value="glb">glb</SelectItem>
                          <SelectItem value="día">día</SelectItem>
                          <SelectItem value="mes">mes</SelectItem>
                          <SelectItem value="hr">hr</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="p-1">
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.quantity}
                        onChange={(e) => handleUpdateItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                        className="h-7 text-xs text-right"
                      />
                    </TableCell>
                    <TableCell className="p-1">
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.unit_cost}
                        onChange={(e) => handleUpdateItem(index, 'unit_cost', parseFloat(e.target.value) || 0)}
                        className="h-7 text-xs text-right"
                      />
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm font-medium text-purple-700">
                      {currencySymbol} {formatNumber(item.total)}
                    </TableCell>
                    <TableCell className="p-1">
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-purple-500 hover:text-purple-700 hover:bg-purple-50"
                          onClick={() => saveAsTemplate(item)}
                          disabled={savingTemplate}
                          title="Guardar como template"
                        >
                          <BookmarkPlus className="w-3 h-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDeleteItem(index)}
                          data-testid={`otros-delete-${index}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="text-center py-4 text-slate-400 text-sm">
            <MoreHorizontal className="w-8 h-8 mx-auto mb-2 opacity-50" />
            No hay items adicionales
          </div>
        )}

        {/* Totals */}
        {otrosItems.length > 0 && (
          <div className="space-y-1 pt-2 border-t border-purple-200">
            <div className="flex justify-between text-sm text-slate-600">
              <span>Subtotal:</span>
              <span className="font-mono">{currencySymbol} {formatNumber(otrosSubtotal)}</span>
            </div>
            <div className="flex justify-between text-sm text-slate-600">
              <span>IGV (18%):</span>
              <span className="font-mono">{currencySymbol} {formatNumber(otrosIgv)}</span>
            </div>
            <div className="flex justify-between text-lg font-bold text-purple-600 pt-1 border-t">
              <span>Total:</span>
              <span className="font-mono" data-testid="otros-total">
                {currencySymbol} {formatNumber(otrosTotal)}
              </span>
            </div>
            {/* Converted amount */}
            <div className="flex justify-between text-sm text-slate-500 pt-1">
              <span>Equivalente (T.C. {formatNumber(exchangeRate)}):</span>
              <span className="font-mono">
                {altCurrencySymbol} {formatNumber(convertedTotal)}
              </span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default OtrosSection;
